import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../components/Modal';
import { CharacterAIRankItem, CharacterStyleStatus } from '../../../types';
import { tosThumbnail } from '../../../utils/tosImage';

interface CharacterAIRankModalProps {
  open: boolean;
  status?: CharacterStyleStatus;       // running / completed / failed
  error?: string;
  items: CharacterAIRankItem[];        // 解析自 castingRankJson
  selectedHash?: string;
  onSelect: (hash: string) => void;
  onConfirm: () => void;
  onRerank: () => void;
  onClose: () => void;
}

const CharacterAIRankModal: React.FC<CharacterAIRankModalProps> = ({
  open, status, error, items, selectedHash, onSelect, onConfirm, onRerank, onClose,
}) => {
  const { t } = useTranslation('projectDetail');
  const loading = status === 'running' || status === 'generating' || status === 'pending';

  return (
    <Modal
      open={open}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap' }}>
          {t('characterStyle.aiRankTitle')}
          <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-3)' }}>
            {t('characterStyle.aiRankTop10Hint')}
          </span>
        </span>
      }
      width="1360px"
      onClose={onClose}
      titleExtra={status === 'failed' ? (
        // 仅失败时给「重新排序」;排序成功后隐藏 —— 该功能成功只执行一次。
        <button className="btn btn-secondary btn-sm" onClick={onRerank}>
          {t('characterStyle.rerank')}
        </button>
      ) : undefined}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>{t('characterStyle.close')}</button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={!selectedHash || loading}>
            {t('characterStyle.confirm')}
          </button>
        </>
      }
    >
      {loading && <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-2)' }}>{t('characterStyle.ranking')}</div>}
      {!loading && status === 'failed' && (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--danger)' }}>
          {t('characterStyle.rankFailed')}{error ? `：${error}` : ''}
        </div>
      )}
      {!loading && status === 'completed' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '1rem' }}>
          {items.map(item => {
            const selected = item.imageHash === selectedHash;
            return (
              <div key={item.imageHash}
                onClick={() => onSelect(item.imageHash)}
                style={{
                  border: selected ? '3px solid var(--primary)' : '1px solid var(--border)',
                  borderRadius: 'var(--radius)', overflow: 'hidden', cursor: 'pointer', background: 'var(--surface-2)',
                }}>
                <div style={{ position: 'relative', aspectRatio: '4/3', background: 'var(--surface-3)' }}>
                  <img src={tosThumbnail(item.imageUrl, 480)} alt={item.name}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  <span style={{
                    position: 'absolute', top: 6, left: 6, background: 'var(--primary)', color: '#fff',
                    borderRadius: 12, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 700,
                  }}>#{item.rank}</span>
                </div>
                <div style={{ padding: '6px 8px', fontSize: '0.76rem', color: 'var(--text-2)', lineHeight: 1.4 }}>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>{item.name}</div>
                  <div>{item.reason}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default CharacterAIRankModal;
